import notification from "antd/es/notification";
import { differenceInDays } from "date-fns";
import { defineMessages } from "react-intl";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import Email from "@mapmycustomers/shared/types/email/Email";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import Organization from "@mapmycustomers/shared/types/Organization";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";

import i18nService from "@app/config/I18nService";
import buildFilters from "@app/scene/reports/components/EmailActivity/utils/buildFilters";
import { getGranularity } from "@app/scene/reports/components/EmailActivity/utils/dateRangeUtils";
import { safeDivide } from "@app/scene/reports/components/EmailActivity/utils/metricUtils";
import DateRangeType from "@app/scene/reports/enums/DateRangeType";
import dateRangeFilterToRange from "@app/scene/reports/utils/dateRangeFilterToRange";
import rangeToDateRange from "@app/scene/reports/utils/rangeToDateRange";
import reportingSettings from "@app/scene/reports/utils/reportingSettings";
import { callApi } from "@app/store/api/callApi";
import { fetchEmailTemplates } from "@app/store/email/actions";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUser, getOrganizationId } from "@app/store/iam";
import { isManager, isOwner } from "@app/store/iam/util";
import {
  getAllTeamsOfEntireOrganization,
  getAllUsersOfEntireOrganization,
  getTeams,
  getTeamsOfEntireOrganization,
} from "@app/store/members";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { formatRawDate } from "@app/util/formatters";

import {
  fetchBreakdown,
  fetchSummary,
  fetchTemplates,
  fetchTrend,
  initialize,
  requestEmailFeature,
  setBreakdownView,
  setDateRange,
  setDateRangeType,
  setScope,
} from "./actions";
import {
  getDateRange,
  getDateRangeType,
  getEmailActivityScope,
  getIsTeamBreakdown,
} from "./selectors";
import { BreakdownRow } from "./types/BreakdownRow";
import { EmailSummaryMetrics } from "./types/EmailSummaryMetrics";
import { TemplatePerformance } from "./types/TemplateRow";
import { TrendDataPoint } from "./types/TrendDataPoint";

const sagaMessages = defineMessages({
  requestSuccess: {
    id: "reports.emailActivity.upsell.requestSuccess",
    defaultMessage: "Request sent. Your admin will be notified.",
    description: "Email Activity - Upsell - toast shown after Notify Admin succeeds",
  },
});

/** API aggregation field names for boolean email-log metrics */
const SUM_FIELDS = ["clicked", "hasBounced", "opened", "replied"];

/**
 * Normalize a bucket-key date to a local-timezone YYYY-MM-DD string.
 * Main trend buckets come as UTC ISO strings ("2026-03-29T18:30:00.000Z");
 * the entity-type companion's `grouped_data` fields come as local date strings
 * ("2026-03-30"). Both represent the same day in the filter's timezone, so
 * normalizing to a local YYYY-MM-DD lets the two maps align for lookup.
 */
const dayKey = (value: string): string => formatRawDate(value, "yyyy-MM-dd");

interface SummaryAggregation {
  sum_clicked?: number;
  sum_hasBounced?: number;
  sum_opened?: number;
  sum_replied?: number;
  total?: number;
}

/**
 * BE puts the primary-group key on a field named after the group-by: `createdBy` when
 * grouped by that field, and the generic `key` when grouped by `teams` (array field).
 */
interface BreakdownAggregation {
  createdBy?: null | User["id"];
  key?: null | Team["id"];
  sum_clicked?: number;
  sum_hasBounced?: number;
  sum_opened?: number;
  sum_replied?: number;
  total?: number;
}

/**
 * Companion response for per-rep / per-team unique companies. Primary group is
 * `emails.entityType`; secondary group is `createdBy` or `teams`. The `accounts`
 * bucket's `grouped_data` holds one entry per rep/team — `field` is the rep/team id
 * and `total` is the distinct-accounts count that row emailed. Keyed on
 * `emails.entityType` as primary because pairing a `teams` primary with a nested
 * secondary returns no per-team rows from the BE — mirroring the shape used by
 * the summary and trend companions, which is known to work.
 */
interface BreakdownEntityTypeAggregation {
  entityType?: EntityType.COMPANY | EntityType.PERSON;
  grouped_data?: Array<{
    /** Team/user id, or `null` for emails not associated with any team / createdBy. */
    field: null | Team["id"] | User["id"];
    total?: number;
  }>;
}

interface TemplateAggregation {
  emailTemplateId?: `${EmailTemplate["id"]}` | null;
  sum_clicked?: number;
  sum_hasBounced?: number;
  sum_opened?: number;
  sum_replied?: number;
  total?: number;
}

interface TrendAggregation {
  day?: string; // a date
  hour?: string; // a date
  month?: string; // a date
  sum_clicked?: number;
  sum_hasBounced?: number;
  sum_opened?: number;
  sum_replied?: number;
  total?: number;
  week?: string; // a date
}

/**
 * Response shape for the companion unique-recipients query.
 * Groups by `emails.entityType` → two buckets: `accounts` + `contacts`.
 * Each secondary-grouped by `createdAt` with a period → per-period counts.
 * The `contacts` bucket also reports `uniqueCompanies` (distinct companies those
 * contacts belong to), both as a top-level sum and per `grouped_data` entry.
 */
interface EntityTypePeriodAggregation {
  entityType?: EntityType.COMPANY | EntityType.PERSON;
  grouped_data?: Array<{
    /** The period bucket key — a local-timezone date string (e.g. "2026-03-30"). */
    field: string;
    total: number;
    uniqueCompanies?: number;
  }>;
  total: number;
  uniqueCompanies?: number;
}

/**
 * Role-based check: a saved scope carried over from a previous session is only
 * restored if it still matches what the current user is allowed to view. Falls
 * through to the role's default scope otherwise (e.g. an owner who saved
 * "Entire Organization" and logged in later as a manager shouldn't silently
 * keep that scope).
 *
 *   Owner   — any scope ({}, teamId, userId) valid
 *   Manager — teamId (for teams they access), or { userId: self } for the Myself view
 *   Rep     — forced to { userId: self }; no scope selector exists
 */
const isScopeValidForUser = (scope: Scope, currentUser: User, accessibleTeams: Team[]): boolean => {
  if (isOwner(currentUser)) {
    return true;
  }
  if (isManager(currentUser)) {
    return (
      (scope.userId === currentUser.id && !scope.teamId) ||
      (!scope.userId && accessibleTeams.some((team) => team.id === scope.teamId))
    );
  }
  return scope.userId === currentUser.id && !scope.teamId;
};

function* onInitialize() {
  const currentUser: User = yield select(getCurrentUser);
  const accessibleTeams: Team[] = yield select(getTeams);

  // Role-based default scope — used when nothing is persisted or the persisted
  // scope is invalid for the current user.
  let scope: Scope = { userId: currentUser.id };
  if (isOwner(currentUser)) {
    scope = {};
  } else if (isManager(currentUser) && accessibleTeams.length) {
    scope = { teamId: accessibleTeams[0].id };
  }

  const saved = reportingSettings.getEmailReportingView();
  if (saved) {
    if (isScopeValidForUser(saved.scope, currentUser, accessibleTeams)) {
      scope = saved.scope;
    }
    // `rangeToDateRange` handles both shapes — a DateRangeType enum value or a
    // "YYYY-MM-DD_YYYY-MM-DD" custom payload. For enum types we restore the type
    // so the reducer regenerates today's range (e.g. "this week" saved yesterday
    // still means "this week" today, not last week's dates); for custom we also
    // dispatch the parsed DateRange since the reducer won't derive it from the type.
    const savedRange = rangeToDateRange(saved.range);
    if (savedRange) {
      const isEnumRange = (Object.values(DateRangeType) as string[]).includes(saved.range);
      const savedType = isEnumRange ? (saved.range as DateRangeType) : DateRangeType.CUSTOM;
      yield put(setDateRangeType(savedType));
      if (savedType === DateRangeType.CUSTOM) {
        yield put(setDateRange(savedRange));
      }
    }
  }

  yield put(setScope(scope));
  yield put(fetchEmailTemplates.request());
}

function* onPersistEmailReportingView() {
  const scope: Scope = yield select(getEmailActivityScope);
  const dateRangeType: DateRangeType = yield select(getDateRangeType);
  const dateRange: DateRange = yield select(getDateRange);
  yield call(reportingSettings.setEmailReportingView, {
    range: dateRangeFilterToRange(dateRange, dateRangeType),
    scope,
  });
}

function* onRequestEmailFeature() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("requestEmailFeature", orgId, { featureKey: "email" });
    yield put(requestEmailFeature.success());
    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({ message: intl.formatMessage(sagaMessages.requestSuccess) });
    }
  } catch (error) {
    yield put(requestEmailFeature.failure());
    yield put(handleError({ error }));
  }
}

function* onFetchSummary() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const dateRange: DateRange = yield select(getDateRange);
    const dateRangeType: DateRangeType = yield select(getDateRangeType);
    const scope: Scope = yield select(getEmailActivityScope);
    const granularity = getGranularity(dateRangeType, dateRange.startDate, dateRange.endDate);
    const teams: Team[] = yield select(getTeamsOfEntireOrganization);
    const filters = buildFilters(dateRange, scope, teams);

    const response: AggregatedListResponse<Email, SummaryAggregation[]> = yield callApi(
      "fetchEmails",
      orgId,
      { $aggs: { count: true, sum: SUM_FIELDS }, $filters: filters, $limit: 0 }
    );

    // Companion query for unique-contact and unique-company totals. Grouping by entityType
    // yields one bucket per type; the `contacts` bucket's `total` is the unique-contact count
    // and `uniqueCompanies` is the distinct-companies sum across those contacts.
    // Isolated in its own try/catch because the API has historically returned 5xx for this
    // specific primaryGroup+secondaryGroup shape under certain scopes — letting that bubble
    // into the outer catch would blank the whole summary (all cards → zero). Defaulting to 0
    // here degrades only the Unique Contacts card while the other five metrics still render.
    let uniqueContacts = 0;
    let uniqueCompanies = 0;
    try {
      const uniqueResponse: AggregatedListResponse<Email, EntityTypePeriodAggregation[]> =
        yield callApi("fetchEmails", orgId, {
          $aggs: {
            count: true,
            primaryGroup: { field: "emails.entityType" },
            secondaryGroup: { field: "createdAt", period: granularity },
          },
          $filters: filters,
          $limit: 0,
        });
      const contactsBucket = (uniqueResponse.aggregations ?? []).find(
        (bucket) => bucket.entityType === EntityType.PERSON
      );
      uniqueContacts = contactsBucket?.total ?? 0;
      uniqueCompanies = contactsBucket?.uniqueCompanies ?? 0;
    } catch {
      // see comment above — degrade the unique-* counts to 0 rather than blanking the card.
    }

    const agg = response.aggregations?.[0];
    const total = agg?.total ?? 0;
    const metrics: EmailSummaryMetrics = {
      bounces: agg?.sum_hasBounced ?? 0,
      clickRate: safeDivide((agg?.sum_clicked ?? 0) * 100, total),
      emailsSent: total,
      openRate: safeDivide((agg?.sum_opened ?? 0) * 100, total),
      replyRate: safeDivide((agg?.sum_replied ?? 0) * 100, total),
      uniqueCompanies,
      uniqueContacts,
    };
    yield put(fetchSummary.success(metrics));
  } catch (error) {
    yield put(fetchSummary.failure());
    yield put(handleError({ error }));
  }
}

function* onFetchBreakdown() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const dateRange: DateRange = yield select(getDateRange);
    const scope: Scope = yield select(getEmailActivityScope);
    const isTeamView: boolean = yield select(getIsTeamBreakdown);
    // Use `getAllUsersOfEntireOrganization` (vs `getUsersOfEntireOrganization`) to
    // include deleted users — reps who sent emails then left the org still need to
    // resolve to name + avatar in historical breakdowns.
    const users: User[] = yield select(getAllUsersOfEntireOrganization);
    const teams: Team[] = yield select(getAllTeamsOfEntireOrganization);
    const filters = buildFilters(dateRange, scope, teams);

    const groupField = isTeamView ? "teams" : "createdBy";
    // BE lands the primary-group key on a field named after the group-by: `createdBy` for
    // rep view, generic `key` for team view (because `teams` is an array field).
    const getBucketId = (agg: BreakdownAggregation) => (isTeamView ? agg.key : agg.createdBy)!;

    const response: AggregatedListResponse<Email, BreakdownAggregation[]> = yield callApi(
      "fetchEmails",
      orgId,
      {
        $aggs: { count: true, primaryGroup: { field: groupField }, sum: SUM_FIELDS },
        $filters: filters,
        $limit: 0,
      }
    );

    // Companion query — per-row unique companies. Primary-groups by entity type
    // (with rep/team as the secondary group) because `teams` as primary + nested
    // secondary returns no per-team rows from the BE. The `accounts` bucket's
    // `grouped_data` holds one entry per rep/team with `total` = distinct accounts
    // that row emailed. Isolated so a 5xx here leaves the companies column at 0
    // without blanking the other metrics.
    let companiesByRowId = new Map<null | Team["id"] | User["id"], number>();
    try {
      const companionResponse: AggregatedListResponse<Email, BreakdownEntityTypeAggregation[]> =
        yield callApi("fetchEmails", orgId, {
          $aggs: {
            count: true,
            primaryGroup: { field: "emails.entityType" },
            secondaryGroup: { field: groupField },
          },
          $filters: filters,
          $limit: 0,
        });
      const accountsBucket = (companionResponse.aggregations ?? []).find(
        (bucket) => bucket.entityType === EntityType.COMPANY
      );
      // `field: null` is a valid bucket — emails not associated with any team (team
      // view) or without a createdBy (rep view). It pairs with the main breakdown's
      // `key: null` / `createdBy: null` row, so keep it in the map.
      companiesByRowId = new Map(
        (accountsBucket?.grouped_data ?? []).map((entry) => [entry.field, entry.total ?? 0])
      );
    } catch {
      // Leave companiesByRowId empty → rows default to 0.
    }

    const days = Math.max(1, differenceInDays(dateRange.endDate, dateRange.startDate));
    const teamMap = new Map(teams.map((team) => [team.id, team.name]));
    const userMap = new Map(users.map((user) => [user.id, user]));

    const rows: BreakdownRow[] = (response.aggregations ?? []).map((agg) => {
      const sent = agg.total ?? 0;
      const rowId = getBucketId(agg);
      const user = isTeamView ? undefined : userMap.get(rowId);
      const resolvedName = isTeamView ? teamMap.get(rowId) : user && userDisplayName(user);
      // First team is sufficient because the breakdown represents the rep's primary
      // affiliation; if a rep is on multiple teams the pill shows their first.
      const primaryTeam = user?.teams?.[0];
      const team = primaryTeam ? { id: primaryTeam.id, name: primaryTeam.name } : undefined;
      return {
        id: rowId,
        avgPerDay: safeDivide(sent, days),
        bounces: agg.sum_hasBounced ?? 0,
        clickRate: safeDivide((agg.sum_clicked ?? 0) * 100, sent),
        companies: companiesByRowId.get(rowId) ?? 0,
        name: resolvedName,
        openRate: safeDivide((agg.sum_opened ?? 0) * 100, sent),
        replyRate: safeDivide((agg.sum_replied ?? 0) * 100, sent),
        sent,
        team,
        user,
      };
    });

    yield put(fetchBreakdown.success(rows));
  } catch (error) {
    yield put(fetchBreakdown.failure());
    yield put(handleError({ error }));
  }
}

function* onFetchTemplates() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const dateRange: DateRange = yield select(getDateRange);
    const scope: Scope = yield select(getEmailActivityScope);
    const teams: Team[] = yield select(getTeamsOfEntireOrganization);
    const filters = buildFilters(dateRange, scope, teams);

    const response: AggregatedListResponse<Email, TemplateAggregation[]> = yield callApi(
      "fetchEmails",
      orgId,
      {
        $aggs: {
          count: true,
          primaryGroup: { field: "emails.emailTemplateId" },
          sum: ["clicked", "opened", "replied"],
        },
        $filters: filters,
        $limit: 0,
      }
    );

    // Grouped by `emails.emailTemplateId` → bucket key lives on `emailTemplateId`.
    // The `null` bucket collects emails sent without a template — skipped.
    // `isNotEmpty` narrows both `null` and `undefined`.
    const aggregationsWithTemplateId = (response.aggregations ?? []).filter((agg) =>
      isNotEmpty(agg.emailTemplateId)
    );

    // BE returns primary-group keys as strings. Convert once to numeric IDs so
    // the table's lookup against the email-store template map hits (the store
    // keys by numeric `template.id`). The component resolves names/body/subject
    // via `getEmailTemplateInfo`, keeping the shared email store as the single
    // source of truth for template data.
    const rows: TemplatePerformance[] = aggregationsWithTemplateId.map((agg) => {
      const sent = agg.total ?? 0;
      return {
        clickRate: safeDivide((agg.sum_clicked ?? 0) * 100, sent),
        openRate: safeDivide((agg.sum_opened ?? 0) * 100, sent),
        replyRate: safeDivide((agg.sum_replied ?? 0) * 100, sent),
        sent,
        templateId: parseInt(agg.emailTemplateId!, 10),
      };
    });

    yield put(fetchTemplates.success(rows));
  } catch (error) {
    yield put(fetchTemplates.failure());
    yield put(handleError({ error }));
  }
}

function* onFetchTrend() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const dateRange: DateRange = yield select(getDateRange);
    const dateRangeType: DateRangeType = yield select(getDateRangeType);
    const scope: Scope = yield select(getEmailActivityScope);
    const period = getGranularity(dateRangeType, dateRange.startDate, dateRange.endDate);
    const teams: Team[] = yield select(getTeamsOfEntireOrganization);
    const filters = buildFilters(dateRange, scope, teams);

    const response: AggregatedListResponse<Email, TrendAggregation[]> = yield callApi(
      "fetchEmails",
      orgId,
      {
        $aggs: {
          count: true,
          primaryGroup: { field: "createdAt", period },
          sum: SUM_FIELDS,
        },
        $filters: filters,
        $limit: 0,
      }
    );

    // Companion query — per-period unique contact counts for the Unique Contacts series.
    // Groups by entityType → contacts bucket's `grouped_data` has one row per period with
    // `total` = distinct contacts emailed that period.
    // Same rationale as the summary companion: the API has historically returned 5xx for
    // the createdAt+entityId shape under certain scopes. Isolate so a failure leaves the
    // Unique Contacts series showing 0 per point without blanking the other four metrics.
    let uniqueContactsByPeriod = new Map<string, number>();
    try {
      const uniqueResponse: AggregatedListResponse<Email, EntityTypePeriodAggregation[]> =
        yield callApi("fetchEmails", orgId, {
          $aggs: {
            count: true,
            primaryGroup: { field: "emails.entityType" },
            secondaryGroup: { field: "createdAt", period },
          },
          $filters: filters,
          $limit: 0,
        });
      const contactsBucket = (uniqueResponse.aggregations ?? []).find(
        (bucket) => bucket.entityType === EntityType.PERSON
      );
      uniqueContactsByPeriod = new Map(
        (contactsBucket?.grouped_data ?? []).map((entry) => [dayKey(entry.field), entry.total])
      );
    } catch {
      // Leave map empty; per-period unique contacts default to 0.
    }

    const data: TrendDataPoint[] = (response.aggregations ?? []).map((agg) => {
      const date = agg[period]!;
      return {
        bounces: agg.sum_hasBounced ?? 0,
        clicked: agg.sum_clicked ?? 0,
        date,
        opened: agg.sum_opened ?? 0,
        replied: agg.sum_replied ?? 0,
        sent: agg.total ?? 0,
        uniqueContacts: uniqueContactsByPeriod.get(dayKey(date)) ?? 0,
      };
    });

    yield put(fetchTrend.success(data));
  } catch (error) {
    yield put(fetchTrend.failure());
    yield put(handleError({ error }));
  }
}

function* onFilterChange() {
  yield put(fetchBreakdown.request());
  yield put(fetchSummary.request());
  yield put(fetchTemplates.request());
  yield put(fetchTrend.request());
}

// Summary, templates, and trend aggregate over the scope regardless of grouping,
// so a rep/team toggle only needs the breakdown to refetch.
function* onBreakdownViewChange() {
  yield put(fetchBreakdown.request());
}

export function* emailActivitySagas() {
  yield all([
    takeLatest(initialize, onInitialize),
    takeLatest(fetchBreakdown.request, onFetchBreakdown),
    takeLatest(fetchSummary.request, onFetchSummary),
    takeLatest(fetchTemplates.request, onFetchTemplates),
    takeLatest(fetchTrend.request, onFetchTrend),
    takeLatest(requestEmailFeature.request, onRequestEmailFeature),
    takeLatest(setBreakdownView, onBreakdownViewChange),
    takeLatest(setDateRange, onFilterChange),
    takeLatest(setDateRangeType, onFilterChange),
    takeLatest(setScope, onFilterChange),
    takeLatest([setDateRange, setDateRangeType, setScope], onPersistEmailReportingView),
  ]);
}
